import { useMemo } from 'react'
import { useMatch } from '../hooks/useMatches'
import { useMatchGames } from '../hooks/useGames'
import { useSideBets } from '../hooks/useSideBets'
import { useRealtimeMatchScores } from '../hooks/useRealtimeScores'
import { useMoneyByPlayer } from '../hooks/useMoneyByPlayer'
import { getGameCourse, getGameTee, getMixedTees } from '../constants/courses'
import { buildGameHandicapBreakdown } from '../utils/buildGameHandicapBreakdown'
import { Trophy } from 'lucide-react'
import { computeRoundResults, extractTeamPoints } from '../utils/scoringEngine'
import { getInitials } from '../utils/colors'

/**
 * Money board showing how much each player is up or down across all rounds in the match.
 */
export default function MoneyBoard({ matchId, viewedSessionId, viewedGameId, onSettleUp }) {
  const { data: match } = useMatch(matchId)
  const { data: allMatchGames } = useMatchGames(matchId)
  const { data: sideBets } = useSideBets(matchId)

  const matchPlayers = match?.match_players || []

  // When viewing a specific session or game, filter accordingly
  const matchGames = useMemo(() => {
    if (!allMatchGames) return allMatchGames
    // Single game view (non-team "enter round")
    if (viewedGameId) return allMatchGames.filter(g => g.id === viewedGameId)
    // Session view (team match "enter session")
    if (viewedSessionId) {
      const session = (match?.sessions || []).find(s => s.id === viewedSessionId)
      if (!session) return allMatchGames
      const groupIds = new Set((session.session_groups || []).map(g => g.id))
      return allMatchGames.filter(g => groupIds.has(g.session_group_id))
    }
    return allMatchGames
  }, [allMatchGames, viewedSessionId, viewedGameId, match])

  // Subscribe to realtime score/bet/ctp changes for in-progress games
  const inProgressGameIds = useMemo(
    () => (matchGames || []).filter(g => g.status === 'in_progress').map(g => g.id),
    [matchGames]
  )
  useRealtimeMatchScores(matchId, inProgressGameIds)

  const moneyByPlayer = useMoneyByPlayer({ match, matchGames, sideBets, viewedSessionId, viewedGameId })

  // Flatten and sort
  const playerList = Object.values(moneyByPlayer).sort((a, b) => b.total - a.total)

  const isTeamMatch = match?.match_type === 'team_match' || match?.match_type === 'invitational'
  const matchTeams = match?.match_teams || []

  // Compute team points for team matches (same logic as EventStandings)
  const teamPointsMap = useMemo(() => {
    if (!isTeamMatch || matchTeams.length === 0 || !matchGames) return new Map()
    const points = new Map()
    matchTeams.forEach(mt => points.set(mt.id, 0))

    const sessionGames = (matchGames || []).filter(g =>
      g.session_group_id && (g.status === 'completed' || g.status === 'in_progress')
    )
    // Dedupe: keep latest game per session_group_id
    const latestByGroup = new Map()
    for (const g of sessionGames) {
      const existing = latestByGroup.get(g.session_group_id)
      if (!existing || new Date(g.created_at) > new Date(existing.created_at)) {
        latestByGroup.set(g.session_group_id, g)
      }
    }

    for (const game of latestByGroup.values()) {
      const gameTeams = game.teams || []
      if (gameTeams.length < 2 || !game.game_type || game.game_type === 'stroke') continue

      const teamStructure = gameTeams
        .sort((a, b) => a.team_order - b.team_order)
        .map(t => ({ name: t.team_name, playerIds: (t.team_players || []).map(tp => tp.player_id) }))

      let results
      if (game.status === 'completed' && game.final_results?.roundResults) {
        results = game.final_results.roundResults
      } else {
        const course = getGameCourse(game)
        const tee = getGameTee(game)
        const hcp = buildGameHandicapBreakdown({
          gameType: game.game_type, teamStructure, matchPlayers: match?.match_players,
          gamePlayers: game.game_players, course, tee, mixedTees: getMixedTees(game),
        })
        const cfg = { ...(game.game_config || {}) }
        if (course) cfg.holePars = course.par
        results = computeRoundResults(game.game_type, teamStructure, game.scores || [], hcp, game.num_holes || 18, cfg)
      }
      if (!results) continue

      const gamePoints = extractTeamPoints(results, teamStructure.length)
      if (!gamePoints) continue

      const sortedGameTeams = gameTeams.sort((a, b) => a.team_order - b.team_order)
      for (let i = 0; i < sortedGameTeams.length; i++) {
        const mt = matchTeams.find(m => m.team_name === sortedGameTeams[i].team_name)
        if (mt && points.has(mt.id)) {
          points.set(mt.id, points.get(mt.id) + (gamePoints[i] || 0))
        }
      }
    }
    return points
  }, [isTeamMatch, matchTeams, matchGames, match?.match_players])

  // Group players by team for team matches, sorted by team points
  const teamGroups = useMemo(() => {
    if (!isTeamMatch || matchTeams.length === 0) return null
    return matchTeams
      .map(mt => {
        const teamPlayerIds = new Set((mt.match_team_players || []).map(mtp => mtp.player_id))
        const players = playerList.filter(entry => teamPlayerIds.has(entry.playerId))
        const pts = teamPointsMap.get(mt.id) || 0
        return { matchTeam: mt, players, points: pts }
      })
      .sort((a, b) => b.points - a.points)
  }, [isTeamMatch, matchTeams, playerList, teamPointsMap])

  if (!matchId || matchPlayers.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
        No event selected
      </div>
    )
  }

  const renderPlayerRow = (entry, index, listLength) => {
    const { player, total, buyIn } = entry
    if (!player) return null

    return (
      <div
        key={entry.playerId}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '0.75rem 1rem',
          borderBottom: index < listLength - 1 ? '1px solid var(--border-color)' : 'none',
        }}
      >
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          backgroundColor: player.avatar_color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: '700',
          fontSize: '0.75rem',
          color: '#fff',
          flexShrink: 0,
        }}>
          {getInitials(player)}
        </div>
        <span style={{ flex: 1, fontWeight: '500', fontSize: '0.9375rem' }}>
          {player.display_name}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{
            fontSize: '0.875rem',
            fontWeight: '500',
            color: 'var(--text-secondary)',
            width: '3rem',
            textAlign: 'right',
          }}>
            {buyIn > 0 ? `$${buyIn.toFixed(0)}` : '–'}
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '4.5rem' }}>
            <span style={{
              fontWeight: '700',
              fontSize: '1rem',
              color: total > 0
                ? 'var(--accent-primary)'
                : total < 0
                  ? 'var(--accent-danger)'
                  : 'var(--text-muted)',
            }}>
              {total === 0 ? '$0' : total > 0 ? `+$${total.toFixed(0)}` : `-$${Math.abs(total).toFixed(0)}`}
            </span>
            {entry.sideBets !== 0 && (
              <span style={{
                fontSize: '0.625rem',
                color: 'var(--text-muted)',
                marginTop: '0.0625rem',
                whiteSpace: 'nowrap',
              }}>
                {`side games: ${entry.sideBets > 0 ? '+' : '-'}$${Math.abs(entry.sideBets).toFixed(0)}`}
              </span>
            )}
          </div>
        </div>
      </div>
    )
  }

  const columnHeaders = (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.5rem 1rem',
      borderBottom: '1px solid var(--border-color)',
    }}>
      <div style={{ width: '32px', flexShrink: 0 }} />
      <span style={{ flex: 1 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <span style={{
          fontSize: '0.6875rem',
          fontWeight: '600',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          width: '3rem',
          textAlign: 'right',
        }}>
          Buy-In
        </span>
        <span style={{
          fontSize: '0.6875rem',
          fontWeight: '600',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          width: '4.5rem',
          textAlign: 'right',
        }}>
          Net
        </span>
      </div>
    </div>
  )

  const totalPot = playerList.reduce((sum, entry) => sum + entry.buyIn, 0)

  return (
    <div>
      <h1 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '1rem' }}>
        Money Board
      </h1>

      {totalPot > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.75rem 1rem',
          marginBottom: '0.75rem',
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '0.75rem',
          border: '1px solid var(--border-color)',
        }}>
          <span style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>
            Total Pot
          </span>
          <span style={{ fontSize: '1.125rem', fontWeight: '700', color: 'var(--accent-primary)' }}>
            ${totalPot.toFixed(0)}
          </span>
        </div>
      )}

      {teamGroups ? (() => {
        const maxPts = Math.max(...teamGroups.map(g => g.points))
        const leadersCount = teamGroups.filter(g => g.points === maxPts).length
        const isDecided = match?.status === 'completed'
        const hasWinner = isDecided && maxPts > 0 && leadersCount === 1

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {teamGroups.map(({ matchTeam, players, points }) => {
              const isLeader = points === maxPts && points > 0
              const isWinner = hasWinner && isLeader

              return (
                <div
                  key={matchTeam.id}
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    borderRadius: '0.75rem',
                    border: `1px solid ${isLeader ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.4)' : 'var(--border-color)'}`,
                    overflow: 'hidden',
                  }}
                >
                  {/* Team header */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.625rem 1rem',
                    borderBottom: '1px solid var(--border-color)',
                    backgroundColor: isLeader ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.08)' : 'var(--bg-tertiary)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                      {isWinner && <Trophy size={16} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />}
                      <span style={{
                        fontWeight: '700',
                        fontSize: '0.9375rem',
                        color: isLeader ? 'var(--accent-primary)' : 'var(--text-primary)',
                      }}>
                        {matchTeam.team_name}
                      </span>
                    </div>
                    <span style={{
                      fontWeight: '700',
                      fontSize: '0.875rem',
                      color: isLeader ? 'var(--accent-primary)' : 'var(--text-secondary)',
                    }}>
                      {Number.isInteger(points) ? points : points.toFixed(1)} pts
                    </span>
                  </div>
                  {columnHeaders}
                  {players.map((entry, index) => renderPlayerRow(entry, index, players.length))}
                  {players.length === 0 && (
                    <div style={{ padding: '0.75rem 1rem', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                      No players
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )
      })() : (
        <div style={{
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '0.75rem',
          border: '1px solid var(--border-color)',
          overflow: 'hidden',
        }}>
          {columnHeaders}
          {playerList.map((entry, index) => renderPlayerRow(entry, index, playerList.length))}
        </div>
      )}

      <p style={{
        textAlign: 'center',
        fontSize: '0.6875rem',
        color: 'var(--text-muted)',
        marginTop: '0.75rem',
      }}>
        Updates live as games are decided
      </p>

      {match?.status === 'completed' && onSettleUp && (
        <div style={{
          position: 'fixed',
          bottom: '76px',
          left: 0,
          right: 0,
          padding: '0.75rem 1rem 0.75rem',
          backgroundColor: 'var(--bg-primary)',
          borderTop: '1px solid var(--border-color)',
          zIndex: 10,
        }}>
          <button
            onClick={onSettleUp}
            style={{
              width: '100%',
              padding: '0.875rem',
              borderRadius: '9999px',
              backgroundColor: 'var(--accent-primary)',
              color: '#fff',
              fontWeight: '700',
              fontSize: '1rem',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Settle Up
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Inline money summary for a single completed round.
 * Shows each player's +/- for that round.
 */
export function RoundMoneySummary({ gameData, matchPlayers, presses }) {
  const teams = gameData?.teams || []
  const scores = gameData?.scores || []
  const gameType = gameData?.game_type
  const wager = Number(gameData?.wager) || 0
  const numHoles = gameData?.num_holes || 18

  const moneyResults = useMemo(() => {
    if (!gameType || gameType === 'stroke' || wager <= 0 || teams.length < 2) return []

    const teamStructure = teams
      .sort((a, b) => a.team_order - b.team_order)
      .map((t) => ({
        name: t.team_name,
        playerIds: (t.team_players || []).map((tp) => tp.player_id),
      }))

    // Build handicap breakdown
    const course = getGameCourse(gameData)
    const tee = getGameTee(gameData)
    const handicapBreakdown = buildGameHandicapBreakdown({
      gameType, teamStructure, matchPlayers,
      gamePlayers: gameData?.game_players || [], course, tee,
      mixedTees: getMixedTees(gameData),
    })

    const pressData = (presses || []).map((p) => ({
      segment: p.metadata?.segment,
      startHole: p.hole_start,
      wager: Number(p.amount),
    }))

    const cfg = { ...(gameData?.game_config || {}) }
    if (course) cfg.holePars = course.par
    const results = computeRoundResults(gameType, teamStructure, scores, handicapBreakdown, numHoles, cfg)
    return computeMoneyResults(results, teamStructure, wager, gameType, pressData, { nassauWeights: gameData?.game_config?.nassauWeights })
  }, [gameType, teams, scores, wager, numHoles, gameData, matchPlayers, presses])

  if (moneyResults.length === 0) return null

  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.375rem',
      padding: '0.5rem 0',
    }}>
      {moneyResults
        .sort((a, b) => b.amount - a.amount)
        .map(({ playerId, amount }) => {
          const mp = matchPlayers?.find((m) => m.player_id === playerId)
          const player = mp?.player
          if (!player) return null

          return (
            <span
              key={playerId}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.25rem 0.5rem',
                borderRadius: '1rem',
                backgroundColor: amount > 0
                  ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.1)'
                  : amount < 0
                    ? 'rgba(239, 68, 68, 0.1)'
                    : 'var(--bg-tertiary)',
                fontSize: '0.6875rem',
                fontWeight: '600',
                color: amount > 0
                  ? 'var(--accent-primary)'
                  : amount < 0
                    ? 'var(--accent-danger)'
                    : 'var(--text-muted)',
              }}
            >
              {player.display_name.split(' ')[0]}:
              {amount === 0 ? ' $0' : amount > 0 ? ` +$${amount.toFixed(0)}` : ` -$${Math.abs(amount).toFixed(0)}`}
            </span>
          )
        })}

    </div>
  )
}
